import { GraphQLQuery } from "@aws-amplify/api";
import { useAuthenticator } from "@aws-amplify/ui-react";
import {
  FetchUserAttributesOutput,
  fetchUserAttributes,
} from "aws-amplify/auth";
import { generateClient } from "aws-amplify/api";
import { useUserProvider } from "@/utils/UserProvider";
import { createCase } from "@/graphql/mutations";
import { updateUser } from "@/graphql/mutations";
import { getLocation } from "@/graphql/queries";
import {
  CreateCaseInput,
  CreateCaseMutation,
  GetLocationQuery,
  UpdateUserInput,
  UpdateUserMutation,
} from "@/API";
import { databaseMigrationAdapter } from "@/services/DatabaseMigrationAdapter"; // Updated import

const client = generateClient();

const useCreateCase = () => {
  const userSelector = (context: { user: any }) => [context.user]; // Keep any for compatibility
  const { user } = useAuthenticator(userSelector);
  const { amplifyUser } = useUserProvider();

  const handleCreateCase = async (
    title: string,
    description: string,
    patientId: string,
    useTemplate: boolean,
    caseTemplateId: string,
    options?: { ensureRelationship?: boolean }
  ) => {
    const userInfo: FetchUserAttributesOutput = await fetchUserAttributes();
    const locationData = await client.graphql<GraphQLQuery<GetLocationQuery>>({
      query: getLocation,
      variables: { id: userInfo["custom:location"] },
    });
    const location = locationData.data.getLocation;

    const newCase: CreateCaseInput = {
      title: title,
      description: description,
      pID: patientId,
      lID: location?.id,
      owners: [user.username],
    };
    if (location && location.groupUsers) {
      newCase["groups"] = [location.groupUsers, location.groupAdmins!];
    }
    if (useTemplate) newCase.caseTemplateId = caseTemplateId;

    console.log('[REACT-HOOK] Calling Amplify createCase GraphQL mutation');
    const createCaseResponse = await client.graphql<
      GraphQLQuery<CreateCaseMutation>
    >({ query: createCase, variables: { input: newCase } });

            console.log('[REACT-HOOK] Amplify case creation successful, calling database migration service');
    // Relationship-aware migration
    try {
      if (options?.ensureRelationship) {
        const { RelationshipMigrationService } = await import("@/services/RelationshipMigrationService");
        const orchestrator = RelationshipMigrationService.getInstance();
        await orchestrator.migrateCaseEnsuringPatient(
          { ...newCase, id: (createCaseResponse as any)?.data?.createCase?.id },
          createCaseResponse as unknown as Record<string, unknown>
        );
      } else {
        await databaseMigrationAdapter.handleCaseOperation('CREATE', newCase, createCaseResponse as unknown as Record<string, unknown>);
      }
    } catch (e) {
      // Non-blocking
    }

    const updatedUser: UpdateUserInput = {
      id: user.username,
      _version: amplifyUser._version,
      casesCreated: amplifyUser.casesCreated + 1,
      currentCasesCreated: amplifyUser.currentCasesCreated + 1,
    };
    await client.graphql<GraphQLQuery<UpdateUserMutation>>({
      query: updateUser,
      variables: { input: updatedUser },
    });
    return createCaseResponse;
  };

  return { handleCreateCase };
};

export default useCreateCase;
